/**
 * 自社の広告の枠（作り手が KUMO のクレジットで自分のゲームの広告を出す・2026-10-22 money/self-serve-ads-dash）の画面の判断。
 *
 * 設計: docs/teams/money/self-serve-ads-plan-2026-10-20.md（§6 の画面）。口の約束: docs/teams/money/self-serve-ads-contract.md（ssa-core が持つ）。
 * 画面: pages/money/AdsListPage.tsx（一覧）・AdsNewPage.tsx（作る 4 段）・AdDetailPage.tsx（数字・止める / 再開）・
 *       pages/StaffAdsReviewPage.tsx（運営の確認）・components/SelfAdHostChoice.tsx（作り手の「どの広告を出すか」）。
 * 口を呼ぶのは selfAdsApi.ts だけ。ここは純粋な関数と型だけ（`node --test` で型の除去だけで動かす = import は拡張子つき・React を読まない）。
 *
 * お金の単位はセント（1 credit = 1¢・値段は税込み）。値段・1 日の上限の範囲は**サーバーの settings を正**にする（ここに $1〜$50 を固定で書かない =
 * 運営が self_ad_settings を変えたら画面もそのまま追いつく）。下の FALLBACK は settings を読めなかった時の見積もりの表示だけに使う。
 */

// ------------------------------------------------------------------ 約束 §2 の型（そのまま写す。変えるときは約束を先に）

export type CampaignStatus = 'draft' | 'reviewing' | 'active' | 'paused' | 'rejected' | 'stopped' | 'ended';
export type PauseReason = 'advertiser' | 'insufficient_credits' | 'target_changed' | 'advertiser_stopped';
export type HostSource = 'kumo_and_adsterra' | 'kumo_only' | 'adsterra_only';

/**
 * 機械 / AI / 運営の「だめ」の理由（約束 §2 の reasons・設計 §4 の禁止の一覧と同じ並び）。
 * hate（差別）・other（そのほか広告に向かない）は 2026-10-22 ssa-founder-1022 でサーバーに足した（AI の中身のチェック）
 */
export const REJECT_REASONS = ['gambling', 'adult', 'violence', 'drugs', 'deceptive', 'impersonation', 'politics', 'hate', 'external_link', 'other', 'missing_banner'] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

export interface SelfAdSettings {
  priceCentsPerMille: number;
  dailyMinCents: number;
  dailyMaxCents: number;
  dailyDefaultCents: number;
  taxPercent: number;
  /** 広告に使えるクレジット（カードの入金と売上から移した分の残り − AI の実行中の押さえ）。招待・運営の付与は入らない */
  spendableCents: number;
  allStopped: boolean;
  /**
   * AI の案を 1 回作るおおよその原価（**約束にまだ無い**・約束への要望 1 = ssa-dash-result.md）。無ければ「作り直す」に値段を出さない
   */
  suggestCostCents?: number;
}

export interface Campaign {
  id: string;
  status: CampaignStatus;
  pauseReason: PauseReason | null;
  targetProjectId: string;
  targetUrl: string;
  targetName: string;
  tagline: string | null;
  banner320AssetId: string | null;
  banner728AssetId: string | null;
  priceCentsPerMille: number;
  dailyCapCents: number;
  totalCapCents: number | null;
  spentCents: number;
  rejectReason: string | null;
  stopReason: string | null;
  autoReview: unknown;
  createdAt: string;
  submittedAt: string | null;
  reviewedAt: string | null;
}

export interface Stats {
  views: number;
  clicks: number;
  chargedCents: number;
}

export interface CampaignDetail {
  campaign: Campaign;
  today: Stats;
  total: Stats;
  days: Array<{ day: string; views: number; clicks: number; chargedCents: number }>;
  hosts: Array<{ projectId: string; name: string; views: number; share: number }>;
}

export interface CheckedTarget {
  projectId: string;
  slug: string;
  name: string;
  url: string;
}

/** GET targets の 1 行（約束 §2・2026-10-22 ssa-finish）。ok: false の reason は check-url と同じエラーのコード */
export interface TargetOption {
  projectId: string;
  name: string;
  slug: string;
  url: string | null;
  ok: boolean;
  reason: string | null;
}

export interface HostPref {
  projectId: string;
  source: HostSource;
  notifiedAt: string | null;
}

/**
 * 旗 house（KUMO の自社の広告だけ）の作り手の「出す / 出さない」（GET / PUT /v1/money/self-ads/house-hosts/:projectId・2026-10-24 house-optout-fill）。
 * 既定は両方 true（出す）。旗 house でないサーバーでは口が 404 = 画面に出さない
 */
export interface HouseHostPref {
  projectId: string;
  banner: boolean;
  rewarded: boolean;
}

export interface StaffReview {
  stage: 'machine' | 'ai' | 'staff' | 'system';
  verdict: 'ok' | 'ng' | 'review';
  reasons: string[];
  note: string | null;
  actor?: string | null;
  createdAt?: string;
}

export type StaffCampaign = Campaign & {
  developerId: string;
  targetSlug: string;
  targetAgeDays: number;
  advertiserTopupCount: number;
  reviews: StaffReview[];
};

// ------------------------------------------------------------------ 道（URL）

export const ADS_PATH = '/money/ads';
export const ADS_NEW_PATH = '/money/ads/new';
export const adPath = (id: string) => `${ADS_PATH}/${encodeURIComponent(id)}`;
/** 作りかけ（draft）・断られた（rejected）広告の続き。?c= に広告の id（段は広告の中身から決める = resumeStep） */
export const adResumePath = (id: string) => `${ADS_NEW_PATH}?c=${encodeURIComponent(id)}`;
/** 運営の確認の画面。作り手の「お金」の枠の外（運営の鍵で呼ぶ・作り手のナビに出さない） */
export const STAFF_ADS_PATH = '/staff/ads';

// ------------------------------------------------------------------ 値段と回数

/** settings を読めなかった時の表示用だけ（約束 §1 の既定と同じ値）。送る値の検証はサーバー */
export const FALLBACK_SETTINGS: Pick<SelfAdSettings, 'priceCentsPerMille' | 'dailyMinCents' | 'dailyMaxCents' | 'dailyDefaultCents'> = {
  priceCentsPerMille: 100,
  dailyMinCents: 100,
  dailyMaxCents: 5000,
  dailyDefaultCents: 200
};

/** その額で出せる回数のおおよそ（サーバーの view_cap = floor(押さえ × 1000 / 値段) と同じ式） */
export function viewsFor(cents: number, priceCentsPerMille: number): number {
  if (!(priceCentsPerMille > 0) || !(cents > 0)) return 0;
  return Math.floor((cents * 1000) / priceCentsPerMille);
}

/**
 * 1 日の上限の選べる額（ドル単位の切りのよい額だけ・範囲は settings）。
 * WHY 自由入力にしないか: 「$2 で 2,000 回」のように回数と並べて一目で比べられる方が、非エンジニアの作り手に分かりやすい（設計 §6 の ▼）。
 *   範囲の端（min / max / 既定）は必ず入れる = 運営が範囲を変えても選べない額が出ない
 */
export function dailyCapOptions(s: Pick<SelfAdSettings, 'dailyMinCents' | 'dailyMaxCents' | 'dailyDefaultCents'>): number[] {
  const steps = [100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000];
  const set = new Set<number>([s.dailyMinCents, s.dailyDefaultCents, s.dailyMaxCents]);
  for (const c of steps) if (c >= s.dailyMinCents && c <= s.dailyMaxCents) set.add(c);
  return [...set].filter((c) => c >= s.dailyMinCents && c <= s.dailyMaxCents).sort((a, b) => a - b);
}

/** 総額の上限の選べる額。null = 上限なし（クレジットか、止めるまで）。1 日の上限より小さい額は出さない（サーバーの invalid_total_cap と同じ決まり） */
export function totalCapOptions(dailyCapCents: number): Array<number | null> {
  const steps = [500, 1000, 2000, 5000, 10000, 20000, 50000];
  return [null, ...steps.filter((c) => c >= dailyCapCents)];
}

export type CapProblem = 'dailyLow' | 'dailyHigh' | 'totalBelowDaily' | null;

/** 送る前の確かめ（表示の言葉を選ぶため。正はサーバーの invalid_daily_cap / invalid_total_cap） */
export function capProblem(daily: number, total: number | null, s: Pick<SelfAdSettings, 'dailyMinCents' | 'dailyMaxCents'>): CapProblem {
  if (daily < s.dailyMinCents) return 'dailyLow';
  if (daily > s.dailyMaxCents) return 'dailyHigh';
  if (total !== null && total < daily) return 'totalBelowDaily';
  return null;
}

/** 残高が今日の分に足りるか。足りなければ配信は止まる（サーバーが paused insufficient_credits にする）= 確かめの段で先に言う */
export function balanceShort(spendableCents: number, dailyCapCents: number): boolean {
  return spendableCents < dailyCapCents;
}

/** クレジットが何日もつか（1 日の上限を毎日使い切った場合・少なめに見積もる）。0 = 今日の分も無い */
export function daysCovered(spendableCents: number, dailyCapCents: number): number {
  if (!(dailyCapCents > 0) || spendableCents <= 0) return 0;
  return Math.floor(spendableCents / dailyCapCents);
}

/** 総額の上限の残り（null = 上限なし） */
export function totalLeftCents(c: Pick<Campaign, 'totalCapCents' | 'spentCents'>): number | null {
  return c.totalCapCents === null ? null : Math.max(0, c.totalCapCents - c.spentCents);
}

/** 今日の残り（1 日の上限 − 今日の見込み）。上限を下げた分は明日からなので、今日の見込みが上限を超えても 0 で止める */
export function todayLeftCents(dailyCapCents: number, todayChargedCents: number): number {
  return Math.max(0, dailyCapCents - todayChargedCents);
}

/** 押された率（%・小数 1 桁の元の値）。表示 0 は null（0% と書かない = 「まだ分からない」） */
export function clickRate(s: Pick<Stats, 'views' | 'clicks'>): number | null {
  return s.views > 0 ? s.clicks / s.views : null;
}

// ------------------------------------------------------------------ 状態

/** 札の色（pill-ok 等の後ろ）。null = 色なし */
export function statusTone(c: Pick<Campaign, 'status' | 'pauseReason'>): 'ok' | 'warn' | 'danger' | null {
  switch (c.status) {
    case 'active':
      return 'ok';
    case 'reviewing':
      return null;
    case 'paused':
      // 自分で止めた = 落ち着いた色。クレジット切れ・行き先の変更 = 何かしてほしい（黄）
      return c.pauseReason === 'advertiser' ? null : 'warn';
    case 'rejected':
    case 'stopped':
      return 'danger';
    default:
      return null;
  }
}

/** 状態の説明の文の鍵の後ろ（ssa.why.<key>）。止まっている理由まで分ける */
export function statusWhy(c: Pick<Campaign, 'status' | 'pauseReason'>): Exclude<CampaignStatus, 'paused'> | `paused_${PauseReason}` {
  if (c.status === 'paused') return `paused_${c.pauseReason ?? 'advertiser'}`;
  return c.status;
}

export const canPause = (c: Pick<Campaign, 'status'>) => c.status === 'active';
/** 再開は「自分で止めた」「クレジットが足りなかった」だけ（約束 §2 resume）。行き先が変わった = 確認し直し、運営が止めた = 戻せない */
export const canResume = (c: Pick<Campaign, 'status' | 'pauseReason'>) =>
  c.status === 'paused' && (c.pauseReason === 'advertiser' || c.pauseReason === 'insufficient_credits');
/** 文・バナーを直せるのは draft / rejected だけ（約束 §2 PATCH） */
export const canEditCreative = (c: Pick<Campaign, 'status'>) => c.status === 'draft' || c.status === 'rejected';
/** 上限はいつでも（下げた分は明日から）。終わった・止められた広告は変えても意味が無いので出さない */
export const canChangeCaps = (c: Pick<Campaign, 'status'>) => !['stopped', 'ended'].includes(c.status);

/** 一覧の並び: 何かしてほしい物（断られた・クレジット切れ・作りかけ）を上に。あとは新しい順（サーバーの順）のまま */
export function sortForList(list: readonly Campaign[]): Campaign[] {
  const rank = (c: Campaign) => {
    if (c.status === 'rejected' || (c.status === 'paused' && c.pauseReason !== 'advertiser')) return 0;
    if (c.status === 'draft') return 1;
    if (c.status === 'active' || c.status === 'reviewing' || c.status === 'paused') return 2;
    return 3;
  };
  return list
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map((x) => x.c);
}

// ------------------------------------------------------------------ 作る 4 段

export type WizardStep = 1 | 2 | 3 | 4;
export const WIZARD_STEPS: readonly WizardStep[] = [1, 2, 3, 4];

/** 中身がそろっているか（submit の時にサーバーが見るのと同じ: バナーはどちらか 1 つ） */
export function hasBanner(c: Pick<Campaign, 'banner320AssetId' | 'banner728AssetId'>): boolean {
  return Boolean(c.banner320AssetId || c.banner728AssetId);
}

/** 作りかけの広告をどの段から続けるか。バナーがまだ = ② の AI の案から・ある = ③ のいくら使うから */
export function resumeStep(c: Pick<Campaign, 'status' | 'banner320AssetId' | 'banner728AssetId'>): WizardStep {
  if (!canEditCreative(c)) return 4;
  return hasBanner(c) ? 3 : 2;
}

/** 一文の長さ（約束: 60 文字まで）。数えは見た目の 1 文字（絵文字・日本語を 1 と数える）。サーバーと数え方がずれたらサーバーが正 */
export const TAGLINE_MAX = 60;
export function taglineLength(s: string): number {
  return [...s].length;
}

// ------------------------------------------------------------------ 選んだゲームの URL

/**
 * 配信の URL（hosting の hostUrl = https://<slug>.<配信ドメイン>/）を check-url に渡す形にする。
 * 何も無ければ null（まだ本番に公開していない）。ドメインはコードに書かない = サーバーが返した URL をそのまま使う
 */
export function targetUrlOf(hostUrl: string | null | undefined): string | null {
  if (!hostUrl) return null;
  try {
    const u = new URL(hostUrl);
    if (u.protocol !== 'https:' && u.hostname !== 'localhost') return null;
    return `${u.protocol}//${u.host}/`;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ 作り手の選択

export const HOST_SOURCES: readonly HostSource[] = ['kumo_and_adsterra', 'kumo_only', 'adsterra_only'];

// ------------------------------------------------------------------ 7 日の棒

/**
 * 直近 7 日の棒（古い → 新しい）。サーバーは出た日だけを返しうるので、無い日は 0 で埋める。today は UTC の 'YYYY-MM-DD'
 * WHY UTC か: 日の区切り（押さえ・引く）がサーバーで UTC（約束 §1 self_ad_days）。画面の日と 1 日ずれる人がいるので、棒の下に「UTC」と書く
 */
export function lastSevenDays(days: CampaignDetail['days'], today: string): Array<{ day: string; views: number; chargedCents: number }> {
  const by = new Map(days.map((d) => [d.day, d]));
  const base = Date.parse(`${today}T00:00:00Z`);
  const out: Array<{ day: string; views: number; chargedCents: number }> = [];
  for (let i = 6; i >= 0; i--) {
    const day = new Date(base - i * 86_400_000).toISOString().slice(0, 10);
    const d = by.get(day);
    out.push({ day, views: d?.views ?? 0, chargedCents: d?.chargedCents ?? 0 });
  }
  return out;
}

export const utcToday = (now: Date = new Date()) => now.toISOString().slice(0, 10);

// ------------------------------------------------------------------ 運営の確認

/** 確認の記録から、ある段の最後の判定（ai = AI の中身のチェック・machine = 文の決まりの確認）。無ければ null */
export function lastReview(reviews: readonly StaffReview[], stage: StaffReview['stage']): StaffReview | null {
  for (let i = reviews.length - 1; i >= 0; i--) if (reviews[i]!.stage === stage) return reviews[i]!;
  return null;
}

/** 確認の記録から AI の判定（最後の ai の行）。無ければ null（AI の鍵の無いサーバー = 運営の画面は「AI のチェックなし」と出す） */
export const aiVerdict = (reviews: readonly StaffReview[]) => lastReview(reviews, 'ai');

/** 運営の「だめ」に選べる理由（missing_banner は機械が見るので運営の選択には出さない） */
export const STAFF_REJECT_REASONS: readonly RejectReason[] = REJECT_REASONS.filter((r) => r !== 'missing_banner');

/** 理由の鍵を画面の言葉の鍵の後ろに（知らない理由はそのまま出す = サーバーが理由を足しても落ちない） */
export function isKnownReason(r: string): r is RejectReason {
  return (REJECT_REASONS as readonly string[]).includes(r);
}

// ------------------------------------------------------------------ バナーのアップロード（2026-10-22 ssa-founder-1022・founder「アップロードでもいい」）

/** 枠（帯の <img width height>）と、上げる画像の大きさ（2 倍 = スマホの高精細の画面でぼやけない）。サーバーの selfAdsUpload.ts の決まり（比 ±4%・幅 1〜4 倍）の内側 */
export const BANNER_SLOTS = [
  { size: 320, width: 320, height: 50, outWidth: 640, outHeight: 100 },
  { size: 728, width: 728, height: 90, outWidth: 1456, outHeight: 180 }
] as const;
export type BannerSize = (typeof BANNER_SLOTS)[number]['size'];
/** サーバーが受ける形式（中身はサーバーが頭のバイトで確かめ直す = ここは選ぶ時の案内だけ） */
export const UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** サーバーの 1 枚の上限（selfAdsUpload.ts UPLOAD_MAX_BYTES と同じ） */
export const UPLOAD_MAX_BYTES = 1024 * 1024;
/** 選べる元の画像の上限（ブラウザで読んで切り抜くだけ = 大きすぎる写真で端末を重くしない） */
export const SOURCE_MAX_BYTES = 15 * 1024 * 1024;

/**
 * 1 枚の画像から枠の形を切り抜く所（真ん中を残す「cover」）。WHY 真ん中か: 横長の枠に合わせると上下（縦長の画像）か左右（とても横長の画像）が落ちる。
 * どこを残すかを選ばせる画面は最初の版では作らない（ssa-founder-1022-result.md の残り）= 真ん中に大事な物を置くよう案内する
 */
export function coverCrop(srcWidth: number, srcHeight: number, outWidth: number, outHeight: number): { sx: number; sy: number; sw: number; sh: number } {
  const want = outWidth / outHeight;
  if (srcWidth / srcHeight > want) {
    const sw = Math.round(srcHeight * want);
    return { sx: Math.floor((srcWidth - sw) / 2), sy: 0, sw, sh: srcHeight };
  }
  const sh = Math.round(srcWidth / want);
  return { sx: 0, sy: Math.floor((srcHeight - sh) / 2), sw: srcWidth, sh };
}
